import { useEffect } from 'react';
import { atom, useAtom, useAtomValue } from 'jotai';
import { type GlobalStore } from '../state/zustand';
import type {
  AllLocationsQuery,
  ScenarioTimeline_OrderLocationFragment,
} from '../graphql';
import { useAllLocationsQuery } from '../graphql';
import { cloneDeep, memoize } from 'lodash';
import type { ScenarioLocation } from './planningData';
import { workingHoursSchema } from '../components/core/Core';
import { useCurrentUser } from '../state/zustand';

export type AllLocations = AllLocationsQuery | undefined;

export const allLocationsQueryResult = atom<{
  data: AllLocations;
  loading: boolean;
}>({
  data: undefined,
  loading: true,
});

export const orgLocationOverMasterListAtom = atom((get) => {
  const result = get(allLocationsQueryResult);
  if (!result.data || !result.data.allLocations) {
    return result;
  }

  return filterOrgLocationsOverMasterList(result);
});

export const useAllLocations = (opts?: {
  withoutDuplicatesFromMasterList?: boolean;
}): {
  data: AllLocations;
  loading: boolean;
} => {
  const result = useAtomValue(allLocationsQueryResult);
  const orgLocationOverMasterList = useAtomValue(orgLocationOverMasterListAtom);

  if (opts?.withoutDuplicatesFromMasterList) {
    return orgLocationOverMasterList;
  }

  return result;
};

export const useAllPortsForCurrentOrganization = (skip?: boolean) => {
  const currentUser = useCurrentUser();
  const organizationId = currentUser?.organizationId ?? null;

  return useAllLocationsQuery({
    variables: {
      filter: {
        organisationId: {
          equalTo: organizationId,
        },
      },
    },
    skip: skip || !organizationId,
  });
};

export const locationFragmentToScenarioLocation = memoize(
  (location: ScenarioTimeline_OrderLocationFragment): ScenarioLocation => {
    return {
      ...location,
      workingHours:
        location.workingHours &&
        workingHoursSchema.parse(location.workingHours),
    };
  },
);

export function useGlobalAllLocationsQuery(
  currentUser: undefined | GlobalStore['currentUser'],
) {
  // pre-fetch all locations
  const { data, loading } = useAllLocationsQuery({
    fetchPolicy: 'no-cache',
    skip: !currentUser,
  });
  const [currentLocationResult, setCurrentLocationResult] = useAtom(
    allLocationsQueryResult,
  );

  useEffect(() => {
    // Only set the result from the query once, afterwards we update from subscriptions via the Atom only
    if (!currentLocationResult.data && currentUser && data && !loading) {
      setCurrentLocationResult({
        data,
        loading,
      });
    }
  }, [
    currentUser,
    loading,
    data,
    setCurrentLocationResult,
    currentLocationResult.data,
  ]);
}

/**
 * Removes duplicate locations and has preference for locations owned by organizations (if valid)
 * This function is only called once thanks to the Jotai atom caching mechanism
 */
function filterOrgLocationsOverMasterList(locationData: {
  data: AllLocations;
  loading: boolean;
}) {
  const locationCopied = cloneDeep(locationData);
  const locationMap = new Map();

  if (!locationCopied.data) {
    return locationData;
  }

  if (!locationCopied.data.allLocations) {
    return locationData;
  }

  locationCopied.data.allLocations?.nodes.forEach((location) => {
    if (!location) return;

    const normalizedName = normalizeLocationName(location.name);

    if (!locationMap.has(normalizedName)) {
      locationMap.set(normalizedName, location);
    } else {
      if (location.rootOrg && location.countryId) {
        locationMap.set(normalizedName, location);
      }
    }
  });

  // Convert map values back to array (location nodes)
  locationCopied.data.allLocations.nodes = Array.from(locationMap.values());

  return locationCopied;
}

// Normalizes location names for comparison (removes extra spaces, case insensitive)
function normalizeLocationName(locationName: string) {
  return locationName.trim().toLowerCase().replace(/\s+/g, ' ');
}
